import { toast } from "@/components/ui";
import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import type {
  BillingMethod,
  LabNetwork,
  RequestGroup,
} from "@curai/js-fhir-tools";
import { useMutationState, useQueryClient } from "@tanstack/react-query";
import type { ICD10Code } from "../types";

interface UpdateOrderParams {
  labNetwork: LabNetwork;
  orderNote?: string;
  icd10Codes?: ICD10Code[];
  billingMethod: BillingMethod;
}

interface UpdateOrderResponse {
  requestGroup: RequestGroup;
}

export const useUpdateLabOrder = ({
  patientId,
  encounterId,
  requestGroupId,
}: {
  patientId: string;
  encounterId: string;
  requestGroupId: string;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateOrderResponse, UpdateOrderParams>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.Labs.UPDATE_ORDER({
        patientId,
        encounterId,
        requestGroupId,
      }),
      mutationFn: async (params) => {
        return axiosInstance.put(`/labs/order/v2/${requestGroupId}`, params);
      },
      onSuccess: async () => {
        // The response here will cause a refetch of the actionSummary which
        // controls what actions can be taken by the FE, so we want to await
        // this to ensure both invalidations are complete before returning.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.Chart.Labs.DEFAULT({
              patientId,
              encounterId,
            }),
          }),
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.Chart.Labs.DEFAULT_V2({
              patientId,
              encounterId,
            }),
          }),
        ]);
      },
      onError: () => {
        toast.error("Failed to update lab order.");
      },
    },
  });
};

export const useLabOrderUpdateStatus = ({
  patientId,
  encounterId,
  requestGroupId,
}: {
  patientId: string;
  encounterId: string;
  requestGroupId: string;
}) => {
  const allUpdateOrderMutations = useMutationState({
    filters: {
      mutationKey: QueryKeys.Patient.Chart.Labs.UPDATE_ORDER({
        patientId,
        encounterId,
        requestGroupId,
      }),
    },
  });

  const isUpdatingLabOrder = allUpdateOrderMutations.some(
    (mutation) => mutation.status === "pending",
  );

  const allUpdateTestMutations = useMutationState({
    filters: {
      predicate: (mutation) => {
        const key = mutation.options.mutationKey;
        return (
          Array.isArray(key) &&
          key.length >= 6 &&
          key[0] === "patient" &&
          key[1] === "chart" &&
          key[2] === patientId &&
          key[3] === "labs" &&
          key[4] === encounterId &&
          key[5] === requestGroupId &&
          key[7] === "update-test"
        );
      },
    },
  });

  const isUpdatingTest = allUpdateTestMutations.some(
    (mutation) => mutation.status === "pending",
  );

  const allAddTestMutations = useMutationState({
    filters: {
      mutationKey: QueryKeys.Patient.Chart.Labs.ADD_TEST_TO_ORDER({
        patientId,
        requestGroupId,
      }),
    },
  });

  const isAddingTest = allAddTestMutations.some(
    (mutation) => mutation.status === "pending",
  );

  const allRemoveTestMutations = useMutationState({
    filters: {
      predicate: (mutation) => {
        const key = mutation.options.mutationKey;
        return (
          Array.isArray(key) &&
          key.length >= 6 &&
          key[0] === "patient" &&
          key[1] === "chart" &&
          key[2] === patientId &&
          key[3] === "labs" &&
          key[4] === encounterId &&
          key[5] === requestGroupId &&
          key[7] === "remove-test"
        );
      },
    },
  });

  const isRemovingTest = allRemoveTestMutations.some(
    (mutation) => mutation.status === "pending",
  );

  return {
    isUpdatingLabOrder,
    isUpdatingTest,
    isAddingTest,
    isRemovingTest,
  };
};
